
import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  Eye,
  EyeOff,
  Loader2,
  LockKeyhole,
  UserRound,
  AlertTriangle,
  Apple,
  ArrowRight,
  Crown,
  Check,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

import API from "../../services/api";
import { useAuth } from "../../context/AuthContext";

import Navbar from "../../components/common/Navbar";
import Footer from "../../components/common/Footer";

import logo from "../../assets/logo.png";
import bg from "../../assets/bg.png";

import "../../styles/login.css";

export default function Login() {
  const navigate = useNavigate();

  const {
    login: setAuth,
    isAuthenticated,
    user,
  } = useAuth();

  const role = user?.role?.toLowerCase();

  const [form, setForm] = useState({
    identifier: "",
    password: "",
  });

  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Animación inicial
  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Redirección si ya existe una sesión
  useEffect(() => {
    if (!isAuthenticated) return;

    const redirectPath = localStorage.getItem("redirectAfterLogin");

    if (redirectPath) {
      localStorage.removeItem("redirectAfterLogin");
      navigate(redirectPath, { replace: true });
      return;
    }

    switch (role) {
      case "admin":
        navigate("/admin", { replace: true });
        break;
      case "trabajador":
        navigate("/worker", { replace: true });
        break;
      case "cliente":
        navigate("/catalog", { replace: true });
        break;
      default:
        navigate("/", { replace: true });
    }
  }, [isAuthenticated, role, navigate]);

  // Cambio de campos
  const handleChange = useCallback((event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    setError("");
  }, []);

  // Validación
  const validateForm = useCallback(() => {
    if (!form.identifier.trim()) {
      setError("Ingresa tu correo electrónico o usuario.");
      return false;
    }

    if (!form.password.trim()) {
      setError("Ingresa tu contraseña.");
      return false;
    }

    return true;
  }, [form]);

  // Inicio de sesión
  const loginRequest = useCallback(async () => {
    if (loading || !validateForm()) return;

    setLoading(true);
    setError("");

    try {
      const response = await API.post("auth/login/", {
        identifier: form.identifier.trim(),
        password: form.password,
      });

      const payload = response.data?.data;

      if (!payload?.access || !payload?.user) {
        throw new Error("Respuesta de autenticación inválida.");
      }

      const authData = {
        access: payload.access,
        refresh: payload.refresh,
        user: payload.user,
      };

      localStorage.setItem("rememberMe", rememberMe ? "true" : "false");

      // Se conserva la gestión de sesión de tu AuthContext
      setAuth(authData);

      const redirectPath = localStorage.getItem("redirectAfterLogin");

      if (redirectPath) {
        localStorage.removeItem("redirectAfterLogin");
        navigate(redirectPath, { replace: true });
        return;
      }

      switch (payload.user?.role?.toLowerCase()) {
        case "admin":
          navigate("/admin", { replace: true });
          break;
        case "trabajador":
          navigate("/worker", { replace: true });
          break;
        case "cliente":
          navigate("/catalog", { replace: true });
          break;
        default:
          navigate("/", { replace: true });
      }
    } catch (err) {
      console.error("LOGIN ERROR:", err.response?.data || err);

      setError(
        err.response?.data?.errors?.non_field_errors?.[0] ||
          err.response?.data?.errors?.identifier?.[0] ||
          err.response?.data?.message ||
          err.response?.data?.detail ||
          "No fue posible iniciar sesión. Verifica tus credenciales."
      );
    } finally {
      setLoading(false);
    }
  }, [loading, validateForm, form, rememberMe, setAuth, navigate]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        loginRequest();
      }
    },
    [loginRequest]
  );

  const goToRegister = useCallback(() => {
    navigate("/register");
  }, [navigate]);

  const handleForgotPassword = useCallback(() => {
    navigate("/forgot-password");
  }, [navigate]);

  const handleGoogleLogin = useCallback(() => {
    setError("El inicio de sesión con Google todavía no está configurado.");
  }, []);

  const handleAppleLogin = useCallback(() => {
    setError("El inicio de sesión con Apple todavía no está configurado.");
  }, []);

  return (
    <div
      className={`login-page ${mounted ? "login-page--visible" : ""}`}
      style={{ "--login-bg": `url("${bg}")` }}
    >
      {/* Fondo y efectos visuales */}
      <div className="login-page__overlay" />
      <div className="login-page__noise" />
      <div className="login-page__scanlines" />

      <Navbar />

      <main className="login-main">
        <section className="login-card">
          <div className="login-card__top-line" />

          <div className="login-card__corner login-card__corner--tl" />
          <div className="login-card__corner login-card__corner--tr" />
          <div className="login-card__corner login-card__corner--bl" />
          <div className="login-card__corner login-card__corner--br" />

          {/* Marca */}
          <header className="login-card__header">
            <div className="login-logo-wrap">
              <div className="login-logo-crown">
                <Crown size={27} />
              </div>

              <img
                src={logo}
                alt="La Esquina del Parqués"
                className="login-logo"
              />
            </div>

            <div className="login-heading">
              <div className="login-heading__eyebrow">
                <Sparkles size={14} />
                <span>LA ESQUINA DEL PARQUÉS</span>
              </div>

              <h1>¡QUÉ BUENO TENERTE AQUÍ!</h1>

              <p>Ingresa y prepárate para una buena partida.</p>
            </div>
          </header>

          {/* Formulario */}
          <div className="login-form">
            <label className={`login-input ${form.identifier ? "login-input--filled" : ""}`}>
              <UserRound size={20} className="login-input__icon" />

              <input
                type="text"
                name="identifier"
                value={form.identifier}
                placeholder="Correo electrónico o usuario"
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                disabled={loading}
                autoComplete="username"
                aria-label="Correo electrónico o usuario"
              />
            </label>

            <label className={`login-input ${form.password ? "login-input--filled" : ""}`}>
              <LockKeyhole size={20} className="login-input__icon" />

              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={form.password}
                placeholder="Contraseña"
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                disabled={loading}
                autoComplete="current-password"
                aria-label="Contraseña"
              />

              <button
                type="button"
                className="login-input__toggle"
                onClick={() => setShowPassword((previous) => !previous)}
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
              </button>
            </label>

            <div className="login-options">
              <label className="login-remember">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(event) => setRememberMe(event.target.checked)}
                  disabled={loading}
                />

                <span className="login-checkbox">
                  {rememberMe && <Check size={12} />}
                </span>

                <span>Recordarme</span>
              </label>

              <button
                type="button"
                className="login-forgot"
                onClick={handleForgotPassword}
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>

            {error && (
              <div className="login-error" role="alert">
                <AlertTriangle size={18} />
                <span>{error}</span>
              </div>
            )}

            <button
              type="button"
              className="login-button"
              onClick={loginRequest}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 size={20} className="login-spinner" />
                  <span>VALIDANDO...</span>
                </>
              ) : (
                <>
                  <span>ENTRAR A MI CUENTA</span>
                  <ArrowRight size={21} />
                </>
              )}
            </button>

            <div className="login-security">
              <ShieldCheck size={17} />
              <span>Tu cuenta, protegida · La Esquina del Parqués</span>
            </div>

            <div className="login-divider">
              <span />
              <b>O CONTINÚA CON</b>
              <span />
            </div>

            <button
              type="button"
              className="login-social"
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              <span className="login-social__icon login-social__icon--google">
                <span className="google-letter">G</span>
              </span>
              <span>Continuar con Google</span>
            </button>

            <button
              type="button"
              className="login-social"
              onClick={handleAppleLogin}
              disabled={loading}
            >
              <span className="login-social__icon login-social__icon--apple">
                <Apple size={20} />
              </span>
              <span>Continuar con Apple</span>
            </button>

            <div className="login-register">
              <span>¿Todavía no tienes cuenta?</span>

              <button type="button" onClick={goToRegister}>
                Crear cuenta
              </button>
            </div>
          </div>

          <div className="login-card__bottom">
            <span>BUENOS MOMENTOS</span>
            <i>✦</i>
            <span>GRANDES PARTIDAS</span>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
